export type RolePool =
  | "adviser"
  | "paraplanner"
  | "administrator"
  | "logo"
  | "general";

export type DynamicBindingSource =
  | "assigned_adviser"
  | "assigned_paraplanner"
  | "assigned_administrator";

export type ImageSourceType = "static" | "library" | "dynamic";

export interface ImageDynamicBinding {
  source: DynamicBindingSource;
  groupId: string;
  placeholderAssetId?: string;
}

export interface ImageConfig {
  objectFit?: "fill" | "contain" | "cover" | "none";
  lockAspectRatio?: boolean;
  sourceType?: ImageSourceType;
  assetId?: string;
  dynamicBinding?: ImageDynamicBinding;
}

export type AssetFieldType = "text" | "multiline";

export interface AssetFieldDefinition {
  key: string;
  label: string;
  type: AssetFieldType;
}

export interface CompanyAssetGroup {
  _id: string;
  company_id: string;
  name: string;
  role_pool: RolePool;
  sort_order: number;
  field_schema?: AssetFieldDefinition[];
}

export interface CompanyAsset {
  _id: string;
  company_id: string;
  image_id: string;
  name: string;
  group_id: string;
  match_email?: string | null;
  plannr_uuid?: string | null;
  title?: string | null;
  bio?: string | null;
  custom_fields?: Record<string, string>;
  is_placeholder: boolean;
  url?: string;
  azure_url?: string;
  filename?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
}

export interface RecentImageUsage {
  image_id: string;
  url: string;
  filename?: string;
  used_at: string;
  report_id?: string;
  in_library: boolean;
}

export interface CompanyAssetCreate {
  image_id: string;
  name: string;
  group_id: string;
  match_email?: string;
  plannr_uuid?: string;
  title?: string;
  bio?: string;
  is_placeholder?: boolean;
}

export interface CompanyAssetGroupCreate {
  name: string;
  role_pool: RolePool;
  sort_order?: number;
}

export interface CompanyAssetGroupUpdate {
  name?: string;
  role_pool?: RolePool;
  sort_order?: number;
  field_schema?: AssetFieldDefinition[];
}

export const ROLE_POOL_OPTIONS: Array<{ value: RolePool; label: string }> = [
  { value: "general", label: "General" },
  { value: "logo", label: "Logo" },
  { value: "adviser", label: "Adviser headshots" },
  { value: "paraplanner", label: "Paraplanner headshots" },
  { value: "administrator", label: "Administrator headshots" },
];

export interface CompanyAssetUpdate {
  name?: string;
  group_id?: string;
  match_email?: string;
  plannr_uuid?: string;
  title?: string;
  bio?: string;
  custom_fields?: Record<string, string>;
  is_placeholder?: boolean;
}

export interface DynamicBindingRequest {
  binding_id: string;
  source: DynamicBindingSource;
  group_id: string;
  placeholder_asset_id?: string;
}

export interface ResolvedBinding {
  binding_id: string;
  url?: string | null;
  asset_id?: string | null;
  matched_email?: string | null;
}

export const DYNAMIC_BINDING_OPTIONS: Array<{
  value: DynamicBindingSource;
  label: string;
  defaultGroupRole: RolePool;
}> = [
  {
    value: "assigned_adviser",
    label: "Assigned adviser",
    defaultGroupRole: "adviser",
  },
  {
    value: "assigned_paraplanner",
    label: "Assigned paraplanner",
    defaultGroupRole: "paraplanner",
  },
  {
    value: "assigned_administrator",
    label: "Assigned administrator",
    defaultGroupRole: "administrator",
  },
];

export const DYNAMIC_BINDING_LABELS: Record<DynamicBindingSource, string> = {
  assigned_adviser: "Assigned adviser",
  assigned_paraplanner: "Assigned paraplanner",
  assigned_administrator: "Assigned administrator",
};

export interface StaffFieldBindingRequest {
  binding_id: string;
  source: DynamicBindingSource;
  group_id: string;
  field_key: string;
}

export interface ResolvedStaffField {
  binding_id: string;
  value?: string | null;
  match_method?: string | null;
}

export interface StaffResolveRequest {
  name: string;
  role: "adviser" | "paraplanner" | "administrator";
  email?: string;
  uuid?: string;
}

export interface ResolvedStaffMember {
  name: string;
  role: string;
  title?: string | null;
  bio?: string | null;
  photo_url?: string | null;
  asset_id?: string | null;
  match_method?: string | null;
  match_score?: number | null;
  fields?: Record<string, string | null>;
}

const PERSON_ROLE_POOLS: RolePool[] = [
  "adviser",
  "paraplanner",
  "administrator",
];

export function isPersonRolePool(rolePool: RolePool): boolean {
  return PERSON_ROLE_POOLS.includes(rolePool);
}

export const DEFAULT_PERSON_FIELD_SCHEMA: AssetFieldDefinition[] = [
  { key: "title", label: "Title", type: "text" },
  { key: "bio", label: "Bio", type: "multiline" },
];

/** Fixed per-asset fields for person headshot groups (not in field_schema). */
export const PERSON_SYSTEM_ASSET_FIELDS: Array<{
  key: "match_email" | "plannr_uuid";
  label: string;
  optional: boolean;
}> = [
  { key: "match_email", label: "Match email", optional: true },
  { key: "plannr_uuid", label: "Plannr UUID", optional: true },
];

/** Read-only preview rows for the group fields sidebar (matches asset edit order). */
export const PERSON_HEADSHOT_PREVIEW_FIELDS: Array<{
  label: string;
  optional?: boolean;
  inMentionPicker?: boolean;
}> = [
  { label: "Name" },
  { label: "Match email", optional: true },
  { label: "Plannr UUID", optional: true },
  { label: "Title", inMentionPicker: true },
  { label: "Bio", inMentionPicker: true },
];

export function getGroupFieldSchema(
  group: CompanyAssetGroup
): AssetFieldDefinition[] {
  if (!isPersonRolePool(group.role_pool)) {
    return [];
  }
  return [...DEFAULT_PERSON_FIELD_SCHEMA];
}

export function buildStaffFieldMentionId(
  source: DynamicBindingSource,
  groupId: string,
  fieldKey: string
): string {
  return `staff_field:${source}:${groupId}:${fieldKey}`;
}

export function parseStaffFieldMentionId(
  mentionId: string
): { source: DynamicBindingSource; groupId: string; fieldKey: string } | null {
  if (!mentionId.startsWith("staff_field:")) {
    return null;
  }
  const parts = mentionId.split(":");
  if (parts.length < 4) {
    return null;
  }
  const source = parts[1] as DynamicBindingSource;
  const fieldKey = parts[parts.length - 1];
  const groupId = parts.slice(2, -1).join(":");
  if (!groupId || !fieldKey) {
    return null;
  }
  return { source, groupId, fieldKey };
}
